"use client";

import { usePostHog } from "posthog-js/react";
import { BriefcaseBusiness, FileText, ShieldCheck } from "lucide-react";
import { AttributedLink } from "@/components/marketing/attributed-link";
import { useAppFeatureFlag } from "@/components/feature-flags/feature-flags-provider";
import { trackEvent } from "@/lib/analytics";

export function GrowthRouteChooser() {
  const siteControlEnabled = useAppFeatureFlag("site_control");
  const posthog = usePostHog();

  function handleRouteClick(lane: string, href: string) {
    const payload = {
      source: "growth_route_chooser",
      route_lane: lane,
      href,
    };
    posthog?.capture?.("growth_route_clicked", payload);
    trackEvent("growth_route_clicked", payload);
  }

  const routes = [
    {
      icon: FileText,
      lane: "starter_pro",
      label: "I need paperwork for one job",
      body: "Use the free first job pack for RAMS, CPP and site induction. No card.",
      href: "/first-job-free",
      cta: "See job one",
    },
    {
      icon: BriefcaseBusiness,
      lane: "business",
      label: "I run several jobs or a 10–50-person firm",
      body: "Business gives management their own logins and up to 50 active site users, plus attendance, site files and evidence workflows.",
      href: "/business",
      cta: "See Business",
    },
    ...(siteControlEnabled
      ? [
          {
            icon: ShieldCheck,
            lane: "site_control",
            label: "I manage access, inductions and evidence across live sites",
            body: "Site Control is the premium QR, induction, CSCS, attendance and audit lane for principal contractors.",
            href: "/site-control",
            cta: "Book a walkthrough",
          },
        ]
      : []),
  ];

  return (
    <section className="border-y border-border bg-surface-muted px-4 py-12 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-2xl">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-text-secondary">
            Pick your lane
          </p>
          <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">
            Which route fits the way you work?
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted sm:text-base">
            The free first job proves the paperwork. Business is for builder-run
            firms that need the team and site layer, including 10&ndash;50-person teams.
            {siteControlEnabled
              ? " Site Control stays separate for live principal-contractor access and evidence work."
              : null}
          </p>
        </div>

        <div
          className={`mt-8 grid gap-4 ${
            siteControlEnabled ? "lg:grid-cols-3" : "lg:grid-cols-2"
          }`}
        >
          {routes.map(({ icon: Icon, lane, label, body, href, cta }) => {
            return (
            <AttributedLink
              key={href}
              href={href}
              onClick={() => handleRouteClick(lane, href)}
              className="group rounded-xl border border-border bg-surface p-5 transition-colors hover:border-accent hover:bg-accent-light"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy/5 text-navy group-hover:bg-accent group-hover:text-white">
                <Icon className="h-5 w-5" aria-hidden />
              </span>
              <h3 className="mt-4 text-base font-bold text-navy">{label}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted">{body}</p>
              <span className="mt-4 inline-flex text-sm font-semibold text-brand-700 group-hover:text-accent-dark">
                {cta} &rarr;
              </span>
            </AttributedLink>
            );
          })}
        </div>
      </div>
    </section>
  );
}
